import { Component, computed, input } from '@angular/core';
import { CHART, fmtFull } from './chart-kit';

export interface SharePart {
  label: string;
  color: string;
  value: number;
}

/**
 * Part-to-whole as one horizontal bar: segments separated by a 2px surface
 * gap, each labelled below with its value and share (never inside a segment
 * too small to hold it). With `track` set, it is a meter: one part filled
 * against a dim track of the same hue.
 */
@Component({
  selector: 'app-share-bar',
  template: `
    <div class="share-bar" [class.meter]="track()" role="img" [attr.aria-label]="aria()"
      [style.background]="track() ? trackColor() : null">
      @for (p of pct(); track p.label) {
      @if (p.share > 0) {
      <span class="share-seg" [style.flex-grow]="p.share" [style.background]="p.color"
        [attr.title]="p.label + ': ' + full(p.value) + ' (' + p.pctText + ')'"></span>
      }
      }
      @if (track() && remainder() > 0) { <span class="share-rest" [style.flex-grow]="remainder()"></span> }
    </div>
    <div class="share-legend">
      @for (p of pct(); track p.label) {
      <span class="share-key">
        <span class="swatch" [style.background]="p.color"></span>
        <span class="share-label">{{ p.label }}</span>
        <b>{{ full(p.value) }}</b>
        <span class="share-pct">{{ p.pctText }}</span>
      </span>
      }
    </div>
  `,
  styleUrl: './chart.scss',
})
export class ShareBar {
  readonly parts = input.required<SharePart[]>();
  /** Meter mode: the whole is `track`, and only the listed parts are filled. */
  readonly track = input<number | null>(null);

  readonly full = fmtFull;
  readonly c = CHART;

  private readonly whole = computed(() => this.track() ?? this.parts().reduce((a, p) => a + Math.max(0, p.value), 0));

  readonly pct = computed(() => this.parts().map(p => {
    const share = this.whole() > 0 ? Math.max(0, p.value) / this.whole() : 0;
    return { ...p, share, pctText: `${(share * 100).toFixed(share > 0 && share < 0.01 ? 1 : 0)}%` };
  }));

  readonly remainder = computed(() => Math.max(0, 1 - this.pct().reduce((a, p) => a + p.share, 0)));
  readonly trackColor = computed(() => (this.parts()[0]?.color ?? CHART.muted) + '38');
  readonly aria = computed(() => this.pct().map(p => `${p.label} ${p.pctText}`).join(', '));
}
